/*
 * <healr-timeline> — Timeline.com-style "benefits over time" stepper.
 * Single row of steps in order. The active step widens in place and reveals
 * its inline caption; the rest stay as narrow thumbnails (image + label).
 * Desktop: arrows + thumbnail clicks set the active step (width + fade anim).
 * Mobile: a synced tab row of labels + full-width scroll-snap cards.
 * No external dependencies.
 */
(function () {
  if (customElements.get('healr-timeline')) return;

  class HealrTimeline extends HTMLElement {
    connectedCallback() {
      this.style.display = 'block';
      this.row = this.querySelector('[data-timeline-row]');
      this.items = Array.from(this.querySelectorAll('[data-timeline-item]'));
      this.tabs = Array.from(this.querySelectorAll('[data-timeline-tab]'));
      this.prevBtn = this.querySelector('[data-timeline-prev]');
      this.nextBtn = this.querySelector('[data-timeline-next]');

      this.mqMobile = window.matchMedia('(max-width: 767px)');
      this.active = -1;
      this._raf = null;

      if (!this.items.length) return;

      this.items.forEach((item, i) => {
        item.addEventListener('click', () => this.setActive(i, true));
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.setActive(i, true);
          }
        });
      });

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.setActive(i, true));
      });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.setActive(this.active - 1, true));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.setActive(this.active + 1, true));

      if (this.row) {
        this.row.addEventListener(
          'scroll',
          () => {
            if (!this.mqMobile.matches || this._scrolling) return;
            cancelAnimationFrame(this._raf);
            this._raf = requestAnimationFrame(() => this.syncFromScroll());
          },
          { passive: true }
        );
      }

      this.setActive(0, false);
    }

    setActive(i, smooth) {
      i = Math.max(0, Math.min(this.items.length - 1, i));
      if (i === this.active) return;
      this.active = i;
      this._applyState(i);
      if (this.mqMobile.matches) {
        requestAnimationFrame(() => this._scrollToItem(i, smooth));
      }
    }

    _applyState(i) {
      this.items.forEach((el, idx) => {
        const on = idx === i;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-current', on ? 'true' : 'false');
      });
      this.tabs.forEach((t, idx) => {
        const on = idx === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (this.prevBtn) this.prevBtn.disabled = i === 0;
      if (this.nextBtn) this.nextBtn.disabled = i === this.items.length - 1;
    }

    _scrollToItem(i, smooth) {
      if (!this.row) return;
      const item = this.items[i];
      if (!item) return;
      const left = item.offsetLeft - this.row.offsetLeft;
      this._scrolling = true;
      this.row.scrollTo({ left: Math.max(0, left), behavior: smooth ? 'smooth' : 'auto' });
      clearTimeout(this._scrollEnd);
      this._scrollEnd = setTimeout(() => {
        this._scrolling = false;
      }, 450);
    }

    syncFromScroll() {
      let nearest = 0;
      let best = Infinity;
      this.items.forEach((el, idx) => {
        const d = Math.abs(el.offsetLeft - this.row.offsetLeft - this.row.scrollLeft);
        if (d < best) {
          best = d;
          nearest = idx;
        }
      });
      if (nearest !== this.active) {
        this.active = nearest;
        this._applyState(nearest);
      }
    }
  }

  customElements.define('healr-timeline', HealrTimeline);
})();
